import {
    ArrowRight,
    Award,
    BarChart3,
    Bell,
    CheckCircle,
    Clock,
    Lock,
    Mail,
    MapPin,
    Phone,
    Shield,
    Smartphone,
    Target,
    TrendingUp,
    Users,
    Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/header/Navbar';
import { useAuth } from '../hooks/useAuth';

const About = () => {
    const { user } = useAuth();

    const features = [
        {
            icon: <Zap className="w-6 h-6" />,
            title: "Real-Time Updates",
            description: "Track complaints instantly with live updates and notifications."
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Secure Platform",
            description: "Enterprise-grade security to protect sensitive complaint data."
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "Role-Based Access",
            description: "Different access levels for admins, helpdesk, and technicians."
        },
        {
            icon: <Bell className="w-6 h-6" />,
            title: "Smart Notifications",
            description: "Automated alerts for urgent and delayed complaints."
        },
        {
            icon: <BarChart3 className="w-6 h-6" />,
            title: "Analytics Dashboard",
            description: "Gain insights with detailed reports and statistics."
        },
        {
            icon: <Smartphone className="w-6 h-6" />,
            title: "Mobile Friendly",
            description: "Access the system from any device with responsive design."
        }
    ];

    const stats = [
        { label: 'Complaints Resolved', value: '12,847', icon: <CheckCircle className="w-5 h-5" /> },
        { label: 'Active Users', value: '1,234', icon: <Users className="w-5 h-5" /> },
        { label: 'Response Rate', value: '98%', icon: <TrendingUp className="w-5 h-5" /> },
        { label: 'Avg Resolution Time', value: '4.2h', icon: <Clock className="w-5 h-5" /> }
    ];

    const values = [
        {
            icon: <Target className="w-6 h-6" />,
            title: "Our Mission",
            description: "To provide a seamless complaint management experience that enhances customer satisfaction and operational efficiency."
        },
        {
            icon: <Award className="w-6 h-6" />,
            title: "Our Vision",
            description: "To be the leading complaint management solution, setting the standard for excellence in customer service."
        },
        {
            icon: <Lock className="w-6 h-6" />,
            title: "Our Commitment",
            description: "To deliver reliable, secure, and user-friendly solutions that empower organizations to resolve issues faster."
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
            <Navbar user={user} />

            {/* Hero Section */}
            <section className="relative w-full h-[400px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-700" />
                <div className="absolute inset-0 hero-bg opacity-10" />
                <div className="relative z-10 text-center max-w-4xl px-6">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                        <Shield className="w-4 h-4 text-white" />
                        <span className="text-white text-sm font-medium">About Us</span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight">
                        Transforming Complaint <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-pink-200">
                            Management Experience
                        </span>
                    </h1>
                    <p className="mt-4 text-lg md:text-xl text-gray-200 max-w-2xl mx-auto">
                        Empowering organizations to resolve complaints efficiently with our intelligent tracking and management system.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-4">
                        <Link
                            to="/complaints"
                            className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2"
                        >
                            Get Started
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/contact"
                            className="px-6 py-3 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold rounded-lg border border-white/30 transition-all duration-300"
                        >
                            Contact Us
                        </Link>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <div className="max-w-6xl mx-auto px-6 -mt-8 relative z-10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {stats.map((stat, idx) => (
                        <div
                            key={idx}
                            className="bg-white rounded-2xl shadow-lg p-5 flex items-center gap-4 border border-gray-100 transition-all hover:shadow-xl hover:scale-105"
                        >
                            <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
                                {stat.icon}
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                                <p className="text-sm text-gray-500 font-medium">{stat.label}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Our Values */}
            <div className="max-w-6xl mx-auto px-6 py-16">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                        Our <span className="text-blue-600">Values</span>
                    </h2>
                    <p className="text-gray-500 mt-2 text-lg">The principles that guide our work</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {values.map((item, idx) => (
                        <div
                            key={idx}
                            className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 transition-all hover:shadow-xl hover:scale-105"
                        >
                            <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 mb-4">
                                {item.icon}
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Features */}
            <div className="bg-white py-16">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                            Key <span className="text-blue-600">Features</span>
                        </h2>
                        <p className="text-gray-500 mt-2 text-lg">Everything you need to manage complaints effectively</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, idx) => (
                            <div
                                key={idx}
                                className="group bg-gray-50 rounded-2xl p-6 border border-gray-100 transition-all hover:bg-white hover:shadow-lg hover:border-blue-100"
                            >
                                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 group-hover:bg-blue-100 transition-colors mb-4">
                                    {feature.icon}
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">{feature.title}</h3>
                                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="max-w-6xl mx-auto px-6 py-16">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-12 text-center relative overflow-hidden">
                    <div className="absolute inset-0 hero-bg opacity-10" />
                    <div className="relative z-10">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            Ready to Get Started?
                        </h2>
                        <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-8">
                            Join thousands of organizations already using our complaint management system.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Link
                                to="/complaints"
                                className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
                            >
                                Get Started Free
                            </Link>
                            <Link
                                to="/contact"
                                className="px-6 py-3 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg border border-white/30 hover:bg-white/20 transition-all duration-300"
                            >
                                Contact Sales
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-gray-800 text-gray-300 py-10">
                <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div>
                        <h4 className="text-white text-xl font-bold mb-4">Complaint Manager</h4>
                        <p className="text-sm leading-relaxed">
                            Efficiently track and resolve complaints across all departments.
                        </p>
                    </div>
                    <div>
                        <h5 className="text-white font-semibold mb-3">Quick Links</h5>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/" className="hover:text-white transition-colors">Dashboard</Link></li>
                            <li><Link to="/complaints" className="hover:text-white transition-colors">Complaints</Link></li>
                            <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h5 className="text-white font-semibold mb-3">Contact</h5>
                        <ul className="space-y-2 text-sm">
                            <li className="flex items-center gap-2">
                                <Mail className="w-4 h-4" />
                                support@complaintmgmt.com
                            </li>
                            <li className="flex items-center gap-2">
                                <Phone className="w-4 h-4" />
                                +1 (555) 123-4567
                            </li>
                            <li className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                123 Main St, City
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-gray-700 mt-8 pt-6 text-center text-sm">
                    &copy; {new Date().getFullYear()} Complaint Management System. All rights reserved.
                </div>
            </footer>
        </div>
    );
};

export default About;